import React, { useCallback, useEffect, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import { StatusManager } from 'common/components/StatusManager';
import { ENDPOINTS } from 'features/quick-entry/constants/endpoints';
import { useQuickEntryContext } from 'features/quick-entry/context';
import { useShowAlert } from 'hooks';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import ConditionalActionWrapper from 'UI/components/atoms/ConditionalWrapper';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import FileManager from 'UI/components/organisms/FileManager';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { UIStatus } from 'UI/constants/status';
import { getErrorMessage, VALIDATION_REGEXS } from 'UI/utils';

import { QuickEntryTextField } from '../QuickEntryTextField';

import {
  ALERT_INFO_COPIES,
  CANDIDATE_QUICK_ENTRY_STATUSES,
  COPIES,
  DEFAULT_FORM_VALUES,
  DELETE_API_VERSION,
  FORM_FIELDS,
  QUICK_ENTRY_DIALOG,
  SUBMIT_MODE_CONFIG,
  SUCCESS_STATUS_CODES,
  UPLOAD_CATEGORIES,
  WARNING_CLOSE_DIALOG_COPIES
} from './CandidateQuickEntryDrawer.constants';
import { useStyles } from './CandidateQuickEntryDrawer.styled';
import { buildFormParams, mapQuickEntryFiles } from './CandidateQuickEntryDrawer.utils';

export const CandidateQuickEntryDrawer = ({
  onClose = () => {},
  quickEntryId = null,
  notesTemplate = null
}) => {
  const classes = useStyles();
  const { showAlert } = useShowAlert();
  const { refetchCandidateQuickEntries } = useQuickEntryContext();
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [openDialog, setOpenDialog] = useState(null);
  const [createdId, setCreatedId] = useState(null);
  const isSubmitting = isSaving || isSavingDraft;

  const effectiveId = quickEntryId ?? createdId;
  const isEditing = !!effectiveId;

  const { state } = useFetchWithStatusV2({
    apiInstance: SECONDARY_API_CLIENT,
    endpoint: quickEntryId ? `${ENDPOINTS.quickEntriesById.replace(':id', quickEntryId)}` : null,
    initialStatus: UIStatus.Default
  });
  const quickEntry = state?.results;
  const initialFiles = Array.isArray(quickEntry?.files) ? mapQuickEntryFiles(quickEntry.files) : [];

  const resolvedNotesTemplate =
    typeof notesTemplate === 'string' && notesTemplate.trim()
      ? notesTemplate
      : COPIES.notesTemplate;
  const notesPlaceholder = isEditing ? undefined : resolvedNotesTemplate;
  const form = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onSubmit',
    reValidateMode: 'onBlur'
  });
  const { handleSubmit, register, reset, setValue, unregister } = form;

  useEffect(() => {
    if (!quickEntryId || !quickEntry) return;

    reset({
      ...DEFAULT_FORM_VALUES,
      [FORM_FIELDS.name]: quickEntry.candidateName ?? '',
      [FORM_FIELDS.email]: quickEntry.candidateEmail ?? '',
      [FORM_FIELDS.notes]: quickEntry.notes ?? '',
      [FORM_FIELDS.files]: mapQuickEntryFiles(quickEntry.files)
    });
  }, [quickEntryId, quickEntry, reset]);

  useEffect(() => {
    register({ name: FORM_FIELDS.files });

    return () => {
      unregister(FORM_FIELDS.files);
    };
  }, [register, unregister]);

  const handleFilesChanged = useCallback(
    data => {
      const attachmentsChanged = flatMap(map(data, 'files'));
      setValue(FORM_FIELDS.files, attachmentsChanged);
    },
    [setValue]
  );

  const submitEntry = useCallback(
    async (formData, { status }) => {
      const mode = isEditing ? 'edit' : 'create';
      const { action, method, getEndpoint } = SUBMIT_MODE_CONFIG[mode];
      const isDraft = status === CANDIDATE_QUICK_ENTRY_STATUSES.draft;
      const setLoading = isDraft ? setIsSavingDraft : setIsSaving;

      try {
        setLoading(true);

        const finalParams = {
          ...buildFormParams(formData),
          ...(status && { status })
        };
        const response = await SECONDARY_API_CLIENT[method](getEndpoint(effectiveId), finalParams);

        if (!SUCCESS_STATUS_CODES.includes(response.status)) return;

        setLoading(false);

        if (mode === 'create' && response.data?.id != null) {
          setCreatedId(response.data.id);
        }

        refetchCandidateQuickEntries();
        if (isDraft) {
          showAlert({
            severity: 'success',
            title: COPIES.title,
            body: strings.formatString(COPIES.alert.success, { action })
          });
          return;
        }

        setOpenDialog(QUICK_ENTRY_DIALOG.success);
      } catch (error) {
        setLoading(false);
        showAlert({
          body: getErrorMessage(error, COPIES.alert.error),
          title: COPIES.title,
          severity: 'error'
        });
      }
    },
    [isEditing, effectiveId, refetchCandidateQuickEntries, showAlert]
  );

  const handleSaveEntry = handleSubmit(formData =>
    submitEntry(formData, {
      status: CANDIDATE_QUICK_ENTRY_STATUSES.draft
    })
  );

  const handleCreateDraft = handleSubmit(formData =>
    submitEntry(formData, {
      status: CANDIDATE_QUICK_ENTRY_STATUSES.readyToProcess
    })
  );

  const handleClose = () => {
    if (isSubmitting) return;

    setOpenDialog(QUICK_ENTRY_DIALOG.discard);
  };

  const buttonDefinitions = [
    {
      props: {
        disabled: isSubmitting,
        text: COPIES.actions.cancel,
        variant: 'text',
        onClick: handleClose
      }
    },
    {
      mode: 'save',
      props: {
        disabled: isSaving || state.status === UIStatus.Loading,
        isSaving: isSavingDraft,
        onClick: handleSaveEntry,
        text: COPIES.actions.saveProgress,
        variant: 'outlined'
      }
    },
    {
      mode: 'save',
      props: {
        disabled: isSavingDraft || state.status === UIStatus.Loading,
        isSaving,
        text: COPIES.actions.createDraft,
        variant: 'contained',
        onClick: async () => {
          const isValid = await form.triggerValidation();
          if (isValid) setOpenDialog(QUICK_ENTRY_DIALOG.createDraft);
        }
      }
    }
  ];

  const handleCloseDialog = () => {
    setOpenDialog(null);
    onClose();
  };

  const handleCloseConfirmationDialog = () => {
    setOpenDialog(null);
  };

  const handleConfirmCreateDraft = () => {
    setOpenDialog(null);
    handleCreateDraft();
  };

  const successDialogActions = [
    {
      text: COPIES.successDialog.actionButton,
      onClick: handleCloseDialog
    }
  ];

  const handleConfirmDrawerClose = () => {
    setOpenDialog(null);
    onClose();
  };

  const discardDialogActions = [
    {
      variant: 'outlined',
      text: WARNING_CLOSE_DIALOG_COPIES.actions.discardAndExit,
      onClick: handleConfirmDrawerClose
    },
    {
      text: WARNING_CLOSE_DIALOG_COPIES.actions.cancel,
      onClick: () => setOpenDialog(null)
    }
  ];

  return (
    <FPDrawer
      CardHeaderProps={{ title: COPIES.title }}
      FPDrawerProps={{
        open: true,
        onClose: handleClose,
        classes: { paper: classes.drawerPaper }
      }}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
      hasPaddingContent={false}
      onClose={handleClose}
    >
      <ConditionalActionWrapper
        condition={!!quickEntryId}
        wrapper={c => (
          <StatusManager
            status={state?.status}
            slots={{
              Empty: <EmptyPlaceholder subtitle={COPIES.emptyState.title} />,
              Error: <EmptyPlaceholder title={state?.error} />,
              Loading: <LinearProgress />
            }}
          >
            {c}
          </StatusManager>
        )}
      >
        <FormContext {...form}>
          <div className={classes.content}>
            <Alert severity="info">
              <AlertTitle>{ALERT_INFO_COPIES.title}</AlertTitle>
              {ALERT_INFO_COPIES.message}
            </Alert>

            <QuickEntryTextField
              label={COPIES.fields.name}
              name={FORM_FIELDS.name}
              required
              rows={1}
            />
            <QuickEntryTextField
              label={COPIES.fields.email}
              name={FORM_FIELDS.email}
              rows={1}
              rules={{
                validate: value =>
                  !value?.trim() ||
                  VALIDATION_REGEXS.EMAIL.test(value) ||
                  strings.inventory.quickEntry.validations.invalidEmail
              }}
            />
            <QuickEntryTextField
              label={COPIES.fields.notes}
              multiline
              name={FORM_FIELDS.notes}
              placeholder={notesPlaceholder}
              rows={12}
            />
            <div className={classes.uploads}>
              <FileManager
                apiVersionForDeleting={DELETE_API_VERSION}
                defaultCategories={UPLOAD_CATEGORIES}
                filesEndpoint={Endpoints.Files}
                initialFiles={initialFiles}
                isNewEntity
                layoutInColumns
                module="quickEntryCandidate"
                onAttachmentsChanged={handleFilesChanged}
                showItemDate
                showItemMenu
                viewItemOnClick
              />
            </div>
          </div>
        </FormContext>
      </ConditionalActionWrapper>

      <When condition={openDialog === QUICK_ENTRY_DIALOG.createDraft}>
        <DecisionDialog
          body={
            <>
              <Typography gutterBottom variant="body2">
                {COPIES.createDraftDialog.summaryMessage}
              </Typography>
              <Typography variant="body2">{COPIES.createDraftDialog.nextStepsMessage}</Typography>
            </>
          }
          cancelButtonText={COPIES.createDraftDialog.cancelButton}
          confirmButtonText={COPIES.createDraftDialog.confirmButton}
          isOpened={openDialog === QUICK_ENTRY_DIALOG.createDraft}
          onClose={handleCloseConfirmationDialog}
          onConfirm={handleConfirmCreateDraft}
          showSeverityIcon
          title={COPIES.createDraftDialog.title}
          withButtons="YesNo"
        />
      </When>

      <When condition={openDialog === QUICK_ENTRY_DIALOG.success}>
        <DecisionDialog
          actions={successDialogActions}
          isOpened={openDialog === QUICK_ENTRY_DIALOG.success}
          message={COPIES.successDialog.message}
          onClose={handleCloseDialog}
          onConfirm={handleCloseDialog}
          showSeverityIcon
          severity="success"
          title={COPIES.successDialog.title}
        />
      </When>

      <When condition={openDialog === QUICK_ENTRY_DIALOG.discard}>
        <DecisionDialog
          actions={discardDialogActions}
          isOpened
          message={WARNING_CLOSE_DIALOG_COPIES.message}
          onClose={() => setOpenDialog(null)}
          onConfirm={handleConfirmDrawerClose}
          showSeverityIcon
          title={WARNING_CLOSE_DIALOG_COPIES.title}
        />
      </When>
    </FPDrawer>
  );
};
